import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AuthLayout } from '../Auth/AuthLayout'
import { useAuthStore } from '../../store/authStore'
import { useProperty } from '../../contexts/PropertyContext'
import { Button } from '../../components/ui/button'
import { cn } from '../../lib/utils'
import { GoogleAddressAutocomplete } from '../../components/address/GoogleAddressAutocomplete'
import { createHome } from '../../services/home.api'
import { inviteFamilyMember, ALL_SERVICE_TYPES } from '../../services/family.api'
import { ApiError, apiFetch } from '../../services/api'

// ─── Types ────────────────────────────────────────────────────────────────────

interface FamilyEntry {
  id: string
  name: string
  email: string
}

// ─── Input classes (matching auth screens exactly) ────────────────────────────
const inputCls = cn(
  'flex h-12 w-full rounded-xl border bg-white px-4 py-2 text-sm transition-all',
  'placeholder:text-slate-400 text-slate-900',
  'focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900',
  'border-slate-200 hover:border-slate-300'
)

const labelCls = 'text-xs font-normal uppercase tracking-wide text-slate-500'

// ─── Step indicator ───────────────────────────────────────────────────────────
function StepDots({ step }: { step: number }) {
  return (
    <div className="flex items-center gap-2 justify-center">
      {[1, 2, 3].map((s) => (
        <div
          key={s}
          className={cn(
            'rounded-full transition-all duration-300',
            s === step ? 'h-2 w-6 bg-slate-900' : s < step ? 'h-2 w-2 bg-slate-900' : 'h-2 w-2 bg-slate-200'
          )}
        />
      ))}
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function OnboardingPage() {
  const { t } = useTranslation('onboarding')
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const setUser = useAuthStore((s) => s.setAuth)
  const accessToken = useAuthStore((s) => s.accessToken)
  const refreshToken = useAuthStore((s) => s.refreshToken)
  const { refreshProperties } = useProperty()

  const [step, setStep] = React.useState(1)
  const [isLoading, setIsLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [createdHomeId, setCreatedHomeId] = React.useState<string | null>(null)

  // Step 1 — Home details
  const [homeName, setHomeName] = React.useState('')
  const [address, setAddress] = React.useState('')
  const [city, setCity] = React.useState('')
  const [state, setState] = React.useState('')
  const [zip, setZip] = React.useState('')
  const [country, setCountry] = React.useState('US')

  // Step 2 — Family members
  const [members, setMembers] = React.useState<FamilyEntry[]>([
    { id: '1', name: '', email: '' },
  ])

  // ── Step 1 submit: create home ───────────────────────────────────────────────
  const handleHomeSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!address.trim() || !city.trim() || !state.trim() || !zip.trim()) {
      setError(t('step1.error.required'))
      return
    }

    setIsLoading(true)
    try {
      const home = await createHome({
        name: homeName.trim() || undefined,
        address: address.trim(),
        city: city.trim(),
        state: state.trim(),
        zip: zip.trim(),
        country: country.trim() || 'US',
      })
      setCreatedHomeId(home.id)
      setStep(2)
    } catch (err) {
      if (err instanceof ApiError && err.message) {
        setError(err.message)
      } else {
        setError(t('step1.error.failed'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  // ── Step 2 submit: invite family (or skip) ───────────────────────────────────
  const handleFamilySubmit = async (skip = false) => {
    setError(null)
    setIsLoading(true)
    try {
      if (!skip && createdHomeId) {
        const validMembers = members.filter((m) => m.email.trim())
        for (const member of validMembers) {
          try {
            await inviteFamilyMember({
              homeId: createdHomeId,
              email: member.email.trim(),
              role: 'member',
              inviteeName: member.name.trim() || undefined,
              serviceTypes: ALL_SERVICE_TYPES,
              grantFutureAssets: true,
            })
          } catch {
            // Non-fatal — skip invalid emails silently
          }
        }
      }
      setStep(3)
    } finally {
      setIsLoading(false)
    }
  }

  // ── Step 3: mark onboarding complete → dashboard ─────────────────────────────
  const handleFinish = async () => {
    setIsLoading(true)
    try {
      await apiFetch<{ success: boolean; data: unknown }>(
        '/v1/users/me',
        { method: 'PUT', body: { onboardingComplete: true } }
      )
      // Update the auth store so the onboarding gate is cleared
      if (user && accessToken && refreshToken) {
        setUser({ ...user, onboardingComplete: true }, accessToken, refreshToken)
      }
      // Refresh homes so dashboard shows the new home immediately
      await refreshProperties()
      navigate('/dashboard', { replace: true })
    } catch {
      // Even if the PUT fails, let them in � non-blocking
      await refreshProperties().catch(() => {})
      navigate('/dashboard', { replace: true })
    } finally {
      setIsLoading(false)
    }
  }

  // ── Family member helpers ─────────────────────────────────────────────────────
  const addMember = () =>
    setMembers((prev) => [...prev, { id: String(Date.now()), name: '', email: '' }])

  const removeMember = (id: string) =>
    setMembers((prev) => prev.filter((m) => m.id !== id))

  const updateMember = (id: string, field: 'name' | 'email', value: string) =>
    setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, [field]: value } : m)))

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <AuthLayout>
      <div className="space-y-6">
        {/* Step dots */}
        <StepDots step={step} />

        {/* ── STEP 1: Your Home ─────────────────────────────────────────── */}
        {step === 1 && (
          <div className="space-y-7 animate-fade-in-up stagger-1">
            <div>
              <h2 className="text-2xl font-normal text-slate-900 tracking-tight">
                {t('step1.title')}
              </h2>
              <p className="mt-1.5 text-sm text-slate-500">
                {t('step1.subtitle')}
              </p>
            </div>

            {error && (
              <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50/80 border border-red-200">
                <svg className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm font-medium text-red-900">{error}</p>
              </div>
            )}

            <form onSubmit={handleHomeSubmit} className="space-y-4">
              {/* Home name */}
              <div className="space-y-1">
                <label className={labelCls}>
                  {t('step1.homeName')} <span className="normal-case text-slate-400">{t('step1.homeNameOptional')}</span>
                </label>
                <input
                  type="text"
                  value={homeName}
                  onChange={(e) => setHomeName(e.target.value)}
                  placeholder={t('step1.ph.homeName')}
                  className={inputCls}
                  autoFocus
                />
              </div>

              {/* Address */}
              <div className="space-y-1">
                <label className={labelCls}>{t('step1.streetAddress')}</label>
                <GoogleAddressAutocomplete
                  value={address}
                  onChange={setAddress}
                  onPlaceSelected={(parsed) => {
                    if (parsed.city) setCity(parsed.city)
                    if (parsed.state) setState(parsed.state)
                    if (parsed.zipCode) setZip(parsed.zipCode)
                    if (parsed.countryCode || parsed.country) {
                      setCountry(parsed.countryCode || parsed.country)
                    }
                  }}
                  placeholder={t('step1.ph.address')}
                  className={inputCls}
                  required
                />
              </div>

              {/* City + State row */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className={labelCls}>{t('step1.city')}</label>
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder={t('step1.ph.city')}
                    className={inputCls}
                    required
                  />
                </div>
                <div className="space-y-1">
                  <label className={labelCls}>{t('step1.state')}</label>
                  <input
                    type="text"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    placeholder={t('step1.ph.state')}
                    className={inputCls}
                  />
                </div>
              </div>

              {/* Zip + Country row */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className={labelCls}>{t('step1.zipCode')}</label>
                  <input
                    type="text"
                    value={zip}
                    onChange={(e) => setZip(e.target.value)}
                    placeholder={t('step1.ph.zip')}
                    className={inputCls}
                  />
                </div>
                <div className="space-y-1">
                  <label className={labelCls}>{t('step1.country')}</label>
                  <input
                    type="text"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    placeholder={t('step1.ph.country')}
                    className={inputCls}
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 text-sm font-normal bg-slate-900 hover:bg-slate-800 text-white rounded-xl transition-all duration-200 shadow-sm hover:shadow-md active:scale-[0.98]"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    {t('step1.creating')}
                  </span>
                ) : (
                  t('step1.continue')
                )}
              </Button>
            </form>
          </div>
        )}

        {/* ── STEP 2: Family Members ─────────────────────────────────────── */}
        {step === 2 && (
          <div className="space-y-7 animate-fade-in-up stagger-1">
            <div>
              <h2 className="text-2xl font-normal text-slate-900 tracking-tight">
                {t('step2.title')}
              </h2>
              <p className="mt-1.5 text-sm text-slate-500">
                {t('step2.subtitle')}
              </p>
            </div>

            <div className="space-y-3">
              {members.map((member, idx) => (
                <div key={member.id} className="space-y-2 p-4 rounded-xl border border-slate-100 bg-slate-50/50">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      {t('step2.memberLabel', { n: idx + 1 })}
                    </span>
                    {members.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeMember(member.id)}
                        className="text-slate-400 hover:text-red-500 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                  <input
                    type="text"
                    value={member.name}
                    onChange={(e) => updateMember(member.id, 'name', e.target.value)}
                    placeholder={t('step2.ph.name')}
                    className={cn(inputCls, 'h-10')}
                  />
                  <input
                    type="email"
                    value={member.email}
                    onChange={(e) => updateMember(member.id, 'email', e.target.value)}
                    placeholder={t('step2.ph.email')}
                    className={cn(inputCls, 'h-10')}
                  />
                </div>
              ))}

              <button
                type="button"
                onClick={addMember}
                className="w-full h-10 rounded-xl border border-dashed border-slate-300 text-sm text-slate-500 hover:border-slate-400 hover:text-slate-700 transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {t('step2.addMember')}
              </button>
            </div>

            <div className="space-y-3">
              <Button
                onClick={() => handleFamilySubmit(false)}
                className="w-full h-12 text-sm font-normal bg-slate-900 hover:bg-slate-800 text-white rounded-xl transition-all duration-200 shadow-sm hover:shadow-md active:scale-[0.98]"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    {t('step2.sending')}
                  </span>
                ) : (
                  t('step2.sendInvites')
                )}
              </Button>
              <button
                type="button"
                onClick={() => handleFamilySubmit(true)}
                disabled={isLoading}
                className="w-full text-sm text-slate-400 hover:text-slate-600 transition-colors py-2"
              >
                {t('step2.skip')}
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 3: All done ───────────────────────────────────────────── */}
        {step === 3 && (
          <div className="space-y-7 text-center animate-fade-in-up stagger-1">
            {/* Success icon */}
            <div className="flex justify-center">
              <div className="h-16 w-16 rounded-full bg-emerald-50 flex items-center justify-center">
                <svg className="h-8 w-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>

            <div className="space-y-2">
              <h2 className="text-2xl font-normal text-slate-900 tracking-tight">
                {t('step3.title')}
              </h2>
              <p className="text-sm text-slate-500">
                {t('step3.subtitle')}
              </p>
            </div>

            <Button
              onClick={handleFinish}
              className="w-full h-12 text-sm font-normal bg-slate-900 hover:bg-slate-800 text-white rounded-xl transition-all duration-200 shadow-sm hover:shadow-md active:scale-[0.98]"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    {t('step3.opening')}
                </span>
              ) : (
                'Go to dashboard'
              )}
            </Button>
          </div>
        )}
      </div>
    </AuthLayout>
  )
}
